import type { VisibleDimension } from './buildVisibleDimensions';

export interface DecoratedDimension extends VisibleDimension {
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  isGroupHeader: boolean;
}

/**
 * Pure presentation metadata layer - adds visual flags for borders and grouping
 *
 * @param dimensions - Array of visible dimensions from buildVisibleDimensions
 * @returns Same array with added presentation flags for styling
 */
export function decorateDimensions(
  dimensions: VisibleDimension[],
): DecoratedDimension[] {
  return dimensions.map((dim, idx) => {
    const prevDim = idx > 0 ? dimensions[idx - 1] : null;
    const nextDim = idx < dimensions.length - 1 ? dimensions[idx + 1] : null;

    // Simplified neighbor checks
    const sameAsPrev = prevDim?.primary === dim.primary;
    const sameAsNext = nextDim?.primary === dim.primary;

    // Group header: primary row/col with children visible below it
    const isGroupHeader = dim.sub === null && sameAsNext;

    // First in group: either the group header itself or first sub after header
    const isFirstInGroup = dim.sub === null || !sameAsPrev;

    // Last in group: no more items with same primary
    const isLastInGroup = !sameAsNext;

    return {
      ...dim,
      isFirstInGroup,
      isLastInGroup,
      isGroupHeader,
    };
  });
}
